import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useOrg } from "../contexts/OrgProvider";
import { motion } from "framer-motion";
import {
  Shield, AlertTriangle, CheckCircle2, XCircle, FileCheck,
  ClipboardCheck, Users, Wrench, Award, ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { FW_NAMES as fwNames, FW_COLORS as fwColors } from "../constants/frameworks";

const CertificationReadiness = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const { activeFrameworks } = useOrg();
  const fwEntries = Object.entries(fwNames).filter(([id]) => activeFrameworks.includes(id));
  const navigate = useNavigate();
  const [selectedFw, setSelectedFw] = useState(activeFrameworks[0] || "iso27001");
  const [readiness, setReadiness] = useState(null);
  const [summaries, setSummaries] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummaries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/certification-readiness/summary/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummaries(res.data);
    } catch (error) {
      console.error("Failed:", error);
    }
  }, [token]);

  const fetchReadiness = useCallback(async (fw) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/certification-readiness/${fw}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReadiness(res.data);
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSummaries(); }, [fetchSummaries]);
  useEffect(() => {
    if (activeFrameworks.length > 0 && !activeFrameworks.includes(selectedFw)) {
      setSelectedFw(activeFrameworks[0]);
    }
  }, [activeFrameworks, selectedFw]);
  useEffect(() => { fetchReadiness(selectedFw); }, [selectedFw, fetchReadiness]);

  const getStatusColor = (status) => {
    if (status === "ready") return "text-green-500";
    if (status === "almost_ready") return "text-blue-500";
    if (status === "in_progress") return "text-yellow-500";
    return "text-red-500";
  };

  const getStatusBg = (status) => {
    if (status === "ready") return "bg-green-500/10 border-green-500/30";
    if (status === "almost_ready") return "bg-blue-500/10 border-blue-500/30";
    if (status === "in_progress") return "bg-yellow-500/10 border-yellow-500/30";
    return "bg-red-500/10 border-red-500/30";
  };

  const getScoreColor = (s) => s >= 80 ? "text-green-500" : s >= 60 ? "text-yellow-500" : s >= 30 ? "text-orange-500" : "text-red-500";

  return (
    <div className="p-6 space-y-6" data-testid="certification-readiness-page">
      <PageHeader
        title={t("cert.title")}
        description={t("cert.subtitle")}
      />

      {/* Framework Selector */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {fwEntries.map(([id, name]) => {
          const s = summaries?.[id];
          const isSelected = selectedFw === id;
          return (
            <motion.div key={id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Card className={`border cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary border-primary' : 'border-border hover:border-primary/50'}`}
                onClick={() => setSelectedFw(id)} data-testid={`cert-fw-${id}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: fwColors[id] }} />
                    <span className="text-xs font-medium">{name}</span>
                  </div>
                  <span className={`text-2xl font-heading font-bold ${getScoreColor(s?.readiness_score || 0)}`}>
                    {s?.readiness_score || 0}%
                  </span>
                  <Progress value={s?.readiness_score || 0} className="h-1.5 mt-2" />
                  {s?.blockers_count > 0 && (
                    <p className="text-xs text-red-500 mt-1">{s.blockers_count} blocker(s)</p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {readiness && !loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Score + Status */}
          <div className="space-y-4">
            <Card className={`border ${getStatusBg(readiness.status)}`} data-testid="cert-score-card">
              <CardContent className="pt-6 text-center">
                <Award className={`w-12 h-12 mx-auto mb-3 ${getStatusColor(readiness.status)}`} />
                <p className={`text-6xl font-heading font-bold ${getStatusColor(readiness.status)}`}>
                  {readiness.readiness_score}%
                </p>
                <p className={`text-sm font-medium mt-2 ${getStatusColor(readiness.status)}`}>
                  {readiness.status_label}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{fwNames[selectedFw]}</p>
              </CardContent>
            </Card>

            {/* Breakdown */}
            <Card className="border-border" data-testid="cert-breakdown-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Score Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(readiness.breakdown || {}).map(([key, item]) => (
                  <div key={key}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-muted-foreground">{item.label}</span>
                      <span className={`font-mono font-bold ${getScoreColor(item.score)}`}>{item.score}%</span>
                    </div>
                    <Progress value={item.score} className="h-1.5" />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Weight: {item.weight}%</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Blockers + Actions */}
          <div className="lg:col-span-2 space-y-4">
            {/* Blockers */}
            <Card className="border-border" data-testid="cert-blockers-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Certification Blockers ({readiness.blockers?.length || 0})
                </CardTitle>
                <CardDescription>Issues that must be resolved before certification</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {(readiness.blockers || []).length === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                    <p className="text-sm text-green-600 font-medium">No blockers! You are ready for certification.</p>
                  </div>
                ) : (
                  readiness.blockers.map((b, i) => (
                    <div key={i} className={`p-4 rounded-lg border ${
                      b.severity === 'critical' ? 'bg-red-500/5 border-red-500/30' : 'bg-orange-500/5 border-orange-500/30'
                    }`}>
                      <div className="flex items-start gap-3">
                        <XCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${b.severity === 'critical' ? 'text-red-500' : 'text-orange-500'}`} />
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{b.area}</span>
                            <Badge variant="outline" className={b.severity === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'}>
                              {b.severity}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{b.detail}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Card className="border-border">
                <CardContent className="pt-4 pb-4 text-center">
                  <Shield className="w-6 h-6 mx-auto mb-1 text-blue-500" />
                  <p className="text-xl font-heading font-bold">{readiness.stats?.implemented_controls || 0}/{readiness.stats?.total_controls || 0}</p>
                  <p className="text-xs text-muted-foreground">Controls Implemented</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="pt-4 pb-4 text-center">
                  <FileCheck className="w-6 h-6 mx-auto mb-1 text-green-500" />
                  <p className="text-xl font-heading font-bold">{readiness.stats?.controls_with_evidence || 0}</p>
                  <p className="text-xs text-muted-foreground">With Evidence</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="pt-4 pb-4 text-center">
                  <ClipboardCheck className="w-6 h-6 mx-auto mb-1 text-purple-500" />
                  <p className="text-xl font-heading font-bold">{readiness.stats?.completed_audits || 0}</p>
                  <p className="text-xs text-muted-foreground">Audits Completed</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="pt-4 pb-4 text-center">
                  <AlertTriangle className="w-6 h-6 mx-auto mb-1 text-red-500" />
                  <p className="text-xl font-heading font-bold">{readiness.stats?.open_ncs || 0}</p>
                  <p className="text-xs text-muted-foreground">Open NCs</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="pt-4 pb-4 text-center">
                  <Users className="w-6 h-6 mx-auto mb-1 text-yellow-500" />
                  <p className="text-xl font-heading font-bold">{readiness.stats?.completed_reviews || 0}</p>
                  <p className="text-xs text-muted-foreground">Mgmt Reviews</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="pt-4 pb-4 text-center">
                  <Wrench className="w-6 h-6 mx-auto mb-1 text-teal-500" />
                  <p className="text-xl font-heading font-bold">{readiness.stats?.closed_cas || 0}/{readiness.stats?.total_cas || 0}</p>
                  <p className="text-xs text-muted-foreground">CAs Closed</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card className="border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Next Steps</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {readiness.readiness_score < 50 && (
                  <Button variant="outline" className="w-full justify-between" onClick={() => navigate("/wizard")} data-testid="cert-action-wizard">
                    Start Implementation Wizard <ArrowRight className="w-4 h-4" />
                  </Button>
                )}
                <Button variant="outline" className="w-full justify-between" onClick={() => navigate("/gap-analysis")} data-testid="cert-action-gaps">
                  Review Gap Analysis <ArrowRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" className="w-full justify-between" onClick={() => navigate("/soa")} data-testid="cert-action-soa">
                  Update Statement of Applicability <ArrowRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" className="w-full justify-between" onClick={() => navigate("/audits")} data-testid="cert-action-audit">
                  Schedule Internal Audit <ArrowRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-muted-foreground">Calculating readiness...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationReadiness;
